<script setup lang="ts">
import { computed, toRefs } from 'vue';
import noImage1to1 from '@/assets/noimage-1to1.jpg';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import DocumentBasicInfoLayout from '@/components/common/adp-document/basic-info/document-basic-info-layout.vue';
import DocumentMediaName from '@/components/common/adp-document/basic-info/document-media-name.vue';
import DocumentTitle from '@/components/common/adp-document/basic-info/document-title.vue';
import DocumentSentenceDisplay from '@/components/common/adp-document/document-sentence-display.vue';
import { Report } from '@/types';
import { getHighlightKeywords } from '@/utils/adpDocument';
import { imageErrorHandler } from '@/utils/errorHandler';
import { formatDate } from '@/utils/formatters';

interface Props {
  adpDocument: Report;
  // レポート専用の検索結果ページでは画像を大きく表示する
  largeThumbnail?: boolean;
  size?: 'xm' | 'm' | 'l';
  imageVariant?: 'banner' | 'thumbnail';
  isNarrow?: boolean;
  isRelatedArticle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  largeThumbnail: false,
  size: 'xm',
  imageVariant: 'thumbnail',
  isNarrow: false,
  isRelatedArticle: false,
});

const { adpDocument } = toRefs(props);

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const imageUrl = computed(() => props.adpDocument.image_url || noImage1to1);

const publishedAt = computed(() =>
  props.adpDocument.published_at
    ? formatDate(props.adpDocument.published_at)
    : undefined,
);

const highlightKeywords = computed(() =>
  getHighlightKeywords(props.adpDocument),
);

const onLinkClicked = () => {
  adpDocument.value.is_read = true;
  emit('article-link-clicked');
};

const displayPageNumber = computed(
  () => (adpDocument.value?.page_number ?? 0) > 0,
);
</script>

<template>
  <DocumentBasicInfoLayout
    v-if="size === 'l'"
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  >
    <template #publisher>
      <DocumentMediaName :publisher="adpDocument.site_name" />
    </template>
    <template #dateExtra>
      <div
        v-if="displayPageNumber"
        class="report-page-badge c-text-regular--xs"
      >
        PDF：{{ adpDocument.page_number }}ページ
      </div>
    </template>
    <template #bottom>
      <div v-if="adpDocument.chunk_text" class="report-chunk">
        <DocumentSentenceDisplay
          :text="adpDocument.chunk_text"
          :highlight-keywords="highlightKeywords"
          :line-clamp="3"
        ></DocumentSentenceDisplay>
      </div>
    </template>
  </DocumentBasicInfoLayout>
  <div v-else class="report-basic-info">
    <div class="report-left">
      <div class="report-site-name c-text-regular--s">
        <div class="report-site-name-text">{{ adpDocument.site_name }}</div>
      </div>
      <div class="spacing-04"></div>
      <div class="report-title">
        <DocumentTitle
          :adp-document="adpDocument"
          :max-lines="2"
          hide-pdf-badge
          @article-link-clicked="onLinkClicked"
          @article-link-context-menu="emit('article-link-context-menu', $event)"
        />
      </div>
      <div class="spacing-04"></div>
      <div class="document-sentence-display">
        <DocumentSentenceDisplay
          :text="adpDocument.chunk_text ?? ''"
          :highlight-keywords="highlightKeywords"
          :line-clamp="3"
        ></DocumentSentenceDisplay>
      </div>
      <div class="spacing-04"></div>
      <div class="report-bottom">
        <div class="report-date c-text-regular--s">{{ publishedAt }}</div>
        <div v-if="displayPageNumber" class="report-page c-text-regular--xs">
          PDF：{{ adpDocument.page_number }}ページ
        </div>
      </div>
    </div>
    <div>
      <AdpDocumentCardLink
        :adp-document="adpDocument"
        @article-link-clicked="onLinkClicked"
        @article-link-context-menu="emit('article-link-context-menu', $event)"
      >
        <img
          class="report-thumbnail"
          :class="{ large: largeThumbnail }"
          loading="lazy"
          :src="imageUrl"
          @error="imageErrorHandler($event, '1to1')"
        />
      </AdpDocumentCardLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.report-chunk {
  padding: 0 16px 12px 16px;
}

.report-page-badge {
  margin-left: 4px;
  padding: 0 2px;
  color: $color-gray800;
  font-weight: 400;
  border: 1px solid $color-gray800;
  border-radius: 2px;
}

.report-basic-info {
  width: 100%;
  display: flex;
  column-gap: 16px;
  .report-left {
    width: 100%;
    .report-bottom {
      display: flex;
      color: $color-gray800;
      font-weight: 400;
      column-gap: 8px;
      margin-bottom: 16px;
      .report-page {
        border: 1px solid $color-gray800;
        border-radius: 2px;
        padding: 0 2px;
      }
    }
  }
  .report-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 4px;
    font-weight: 400;
    &.large {
      width: 150px;
      height: 150px;
    }
  }
  .report-site-name {
    color: $color-gray800;
    font-weight: 500;
    display: flex;
    align-items: center;
    .report-site-name-text {
      max-width: 155px;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
    }
  }
  .document-sentence-display {
    border-left: 1px solid $color-gray400;
    padding-left: 13px;
  }
}
</style>
